import React, { useState, useMemo } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { CreateBrokerAccount } from '../../types';
import ErrorAlert from '../common/ErrorAlert';
import Button from '../ui/Button';
import FocusedDialog from '../ui/FocusedDialog';
import { BrokerService } from '../../services/brokerService';
import { isWhiteLabel } from '../../config/tenantConfig';

const FOCUS_RING = isWhiteLabel() ? 'focus:ring-zebu-500 focus:border-zebu-500' : 'focus:ring-blue-500 focus:border-blue-500';
const TOGGLE_FOCUS = isWhiteLabel() ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
const TOGGLE_ON = isWhiteLabel() ? 'bg-zebu-600' : 'bg-blue-600';

interface AddBrokerAccountFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (accountData: CreateBrokerAccount) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const AddBrokerAccountForm: React.FC<AddBrokerAccountFormProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error,
  onClearError,
}) => {
  const [formData, setFormData] = useState<CreateBrokerAccount>({
    broker_name: 'zerodha',
    client_id: '',
    api_key: '',
    api_secret: '',
    capital: 0,
    description: '',
  });
  const [capitalInputValue, setCapitalInputValue] = useState('0');
  
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTotpKey, setShowTotpKey] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof CreateBrokerAccount, string>>>({});

  const supportsAutoLogin = formData.broker_name === 'zerodha' || formData.broker_name === 'zebu';
  // Zebu uses the platform-level OAuth app — api_key/api_secret are not
  // user-provided. Hide the fields and skip their validation for Zebu only.
  const isZebu = formData.broker_name === 'zebu';

  // Get broker-specific labels and instructions
  const brokerInstructions = useMemo(() => {
    return BrokerService.getBrokerInstructions(formData.broker_name);
  }, [formData.broker_name]);

  const handleInputChange = (field: keyof CreateBrokerAccount) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'capital') {
      setCapitalInputValue(String(value));
      const parsedValue = Number(value);
      if (!Number.isNaN(parsedValue)) {
        value = parsedValue;
      } else {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Clear general error
    if (error && onClearError) {
      onClearError();
    }
  };

  const handleCapitalBlur = () => {
    const trimmedValue = capitalInputValue.trim();
    const parsedValue = Number(trimmedValue);
    const finalValue = trimmedValue === '' || Number.isNaN(parsedValue) ? 0 : parsedValue;
    setFormData(prev => ({
      ...prev,
      capital: finalValue,
    }));
    setCapitalInputValue(String(finalValue));
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof CreateBrokerAccount, string>> = {};

    if (!formData.client_id.trim()) {
      errors.client_id = 'Client ID is required';
    }

    if (!isZebu && !formData.api_key.trim()) {
      errors.api_key = 'API Key is required';
    }

    if (!isZebu && !formData.api_secret.trim()) {
      errors.api_secret = 'API Secret is required';
    }

    if (formData.capital <= 0) {
      errors.capital = 'Capital must be greater than 0';
    }

    if (formData.auto_login_enabled) {
      if (!formData.password?.trim()) {
        errors.password = 'Password is required when auto-login is enabled';
      }
      if (!formData.totp_key?.trim()) {
        errors.totp_key = 'TOTP Secret Key is required when auto-login is enabled';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        broker_name: 'zerodha',
        client_id: '',
        api_key: '',
        api_secret: '',
        capital: 0,
        description: '',
        auto_login_enabled: false,
        password: '',
        totp_key: '',
      });
      setCapitalInputValue('0');
      setFieldErrors({});
    } catch (error) {
      // Error handling is managed by parent
    }
  };

  const handleClose = () => {
    setFormData({
      broker_name: 'zerodha',
      client_id: '',
      api_key: '',
      api_secret: '',
      capital: 0,
      description: '',
      auto_login_enabled: false,
      password: '',
      totp_key: '',
    });
    setCapitalInputValue('0');
    setFieldErrors({});
    if (onClearError) {
      onClearError();
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <FocusedDialog
        ariaLabel="Add broker account"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Broker Account
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          {error && (
            <div className="mb-4">
              <ErrorAlert message={error} onClose={onClearError} />
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Broker *
              </label>
              <select
                value={formData.broker_name}
                onChange={handleInputChange('broker_name')}
                className={`block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
              >
                <option value="zerodha">Zerodha</option>
                <option value="angel">Angel One</option>
                <option value="finvasia">Finvasia</option>
                <option value="zebu">Zebu</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Client ID *
              </label>
              <input
                type="text"
                value={formData.client_id}
                onChange={handleInputChange('client_id')}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  fieldErrors.client_id
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="ABC123 (Your broker client ID)"
              />
              {fieldErrors.client_id && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.client_id}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This is the client ID provided by your broker and cannot be changed after creation.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Capital *
              </label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={capitalInputValue}
                onChange={handleInputChange('capital')}
                onBlur={handleCapitalBlur}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  fieldErrors.capital
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="100000"
              />
              {fieldErrors.capital && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.capital}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Amount allocated for trading in this account.
              </p>
            </div>

            {!isZebu && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {brokerInstructions.apiKeyLabel} *
                  </label>
                  <input
                    type="text"
                    value={formData.api_key}
                    onChange={handleInputChange('api_key')}
                    className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      fieldErrors.api_key
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={`Your ${brokerInstructions.apiKeyLabel}`}
                  />
                  {fieldErrors.api_key && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.api_key}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {brokerInstructions.apiSecretLabel} *
                  </label>
                  <div className="relative">
                    <input
                      type={showApiSecret ? 'text' : 'password'}
                      value={formData.api_secret}
                      onChange={handleInputChange('api_secret')}
                      className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        fieldErrors.api_secret
                          ? 'border-red-300 dark:border-red-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder={`Your ${brokerInstructions.apiSecretLabel}`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowApiSecret(!showApiSecret)}
                    >
                      {showApiSecret ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.api_secret && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.api_secret}</p>
                  )}
                </div>
              </>
            )}

            {/* Auto-Login Section — Zerodha and Zebu only */}
            {supportsAutoLogin && (
              <div className="mb-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Auto-Login</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Automatically refresh OAuth token every morning at 8:05 AM
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, auto_login_enabled: !prev.auto_login_enabled, password: '', totp_key: '' }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 ${TOGGLE_FOCUS} focus:ring-offset-2 ${
                      formData.auto_login_enabled ? TOGGLE_ON : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    aria-label="Toggle auto-login"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.auto_login_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {formData.auto_login_enabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Broker Login Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password || ''}
                          onChange={handleInputChange('password')}
                          className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                            fieldErrors.password ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Your broker login password"
                        />
                        <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                      {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        TOTP Secret Key *
                      </label>
                      <div className="relative">
                        <input
                          type={showTotpKey ? 'text' : 'password'}
                          value={formData.totp_key || ''}
                          onChange={handleInputChange('totp_key')}
                          className={`block w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                            fieldErrors.totp_key ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Base32 TOTP seed from authenticator app"
                        />
                        <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowTotpKey(!showTotpKey)}>
                          {showTotpKey ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                      {fieldErrors.totp_key && <p className="mt-1 text-xs text-red-600">{fieldErrors.totp_key}</p>}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        The secret key shown when you set up TOTP in your authenticator app.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={handleInputChange('description')}
                rows={3}
                className={`block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${FOCUS_RING} bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none`}
                placeholder="Optional description for this account"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optional description to help identify this account.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <Button type="submit" disabled={isLoading} isLoading={isLoading}>
                Add Account
              </Button>
            </div>
          </form>
        </div>
      </FocusedDialog>
    </div>
  );
};

export default AddBrokerAccountForm;